import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ========================
// TYPES
// ========================

export interface Employee {
  id: string;
  name: string;
  empCode: string;
  designation: string;
  department: string;
  pan: string;
  bankAccount: string;
  bankName: string;
  dateOfJoining: string;
  status: "Active" | "Inactive";
  salaryStructureId: string;
}

export interface SalaryComponent {
  name: string;
  type: "earning" | "deduction";
  amount: number;
  isPercentage: boolean;
  percentOf?: string; // "basic" for HRA etc.
}

export interface SalaryStructure {
  id: string;
  name: string;
  basic: number;
  hra: number; // % of basic
  da: number; // % of basic
  specialAllowance: number;
  pf: number; // % of basic (deduction)
  esi: number; // % of gross if applicable
  professionalTax: number;
  customComponents: SalaryComponent[];
}

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  month: string; // "2026-03"
  totalDays: number;
  presentDays: number;
  absentDays: number;
  leaveDays: number;
  overtimeHours: number;
}

export type PayslipStatus = "Draft" | "Generated" | "Paid";

export interface Payslip {
  id: string;
  employeeId: string;
  employeeName: string;
  empCode: string;
  month: string;
  // Earnings
  basic: number;
  hra: number;
  da: number;
  specialAllowance: number;
  overtimePay: number;
  grossEarnings: number;
  // Deductions
  pf: number;
  esi: number;
  professionalTax: number;
  tds: number;
  totalDeductions: number;
  // Net
  netSalary: number;
  daysWorked: number;
  totalDays: number;
  status: PayslipStatus;
  generatedAt: string;
  paidAt?: string;
}

// ========================
// TDS CALCULATION (India FY 2025-26 New Regime)
// ========================

export function calculateAnnualTDS(annualIncome: number): number {
  // New Tax Regime slabs FY 2025-26
  const slabs = [
    { limit: 300000, rate: 0 },
    { limit: 700000, rate: 0.05 },
    { limit: 1000000, rate: 0.10 },
    { limit: 1200000, rate: 0.15 },
    { limit: 1500000, rate: 0.20 },
    { limit: Infinity, rate: 0.30 },
  ];

  let tax = 0;
  let remaining = annualIncome;
  let prevLimit = 0;

  for (const slab of slabs) {
    const taxableInSlab = Math.min(remaining, slab.limit - prevLimit);
    if (taxableInSlab <= 0) break;
    tax += taxableInSlab * slab.rate;
    remaining -= taxableInSlab;
    prevLimit = slab.limit;
  }

  // Rebate u/s 87A: No tax if income <= 7L
  if (annualIncome <= 700000) tax = 0;

  // 4% Health & Education Cess
  tax *= 1.04;

  return Math.round(tax);
}

export function calculateMonthlyTDS(annualIncome: number): number {
  return Math.round(calculateAnnualTDS(annualIncome) / 12);
}

// ========================
// SEED DATA
// ========================

const SEED_STRUCTURES: SalaryStructure[] = [
  { id: "ss-1", name: "Senior Level", basic: 40000, hra: 50, da: 10, specialAllowance: 8000, pf: 12, esi: 0, professionalTax: 200, customComponents: [] },
  { id: "ss-2", name: "Mid Level", basic: 25000, hra: 50, da: 10, specialAllowance: 5000, pf: 12, esi: 0.75, professionalTax: 200, customComponents: [] },
  { id: "ss-3", name: "Junior Level", basic: 15000, hra: 50, da: 10, specialAllowance: 3000, pf: 12, esi: 0.75, professionalTax: 200, customComponents: [] },
];

const SEED_EMPLOYEES: Employee[] = [];
const SEED_ATTENDANCE: AttendanceRecord[] = [];
const SEED_PAYSLIPS: Payslip[] = [];

// ========================
// CONTEXT
// ========================

interface PayrollContextType {
  employees: Employee[];
  structures: SalaryStructure[];
  attendance: AttendanceRecord[];
  payslips: Payslip[];
  addEmployee: (data: Omit<Employee, "id" | "status">) => Employee;
  updateAttendance: (empId: string, month: string, data: Partial<AttendanceRecord>) => void;
  generatePayslips: (month: string) => Payslip[];
  markPaid: (id: string) => void;
  downloadPayslipPDF: (payslip: Payslip) => void;
  exportSalaryReport: (month: string) => void;
  getAttendance: (empId: string, month: string) => AttendanceRecord | undefined;
}

const PayrollContext = createContext<PayrollContextType | null>(null);

export function PayrollProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>(SEED_EMPLOYEES);
  const [structures] = useState<SalaryStructure[]>(SEED_STRUCTURES);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>(SEED_ATTENDANCE);
  const [payslips, setPayslips] = useState<Payslip[]>(SEED_PAYSLIPS);

  const addEmployee = useCallback((data: Omit<Employee, "id" | "status">) => {
    const emp: Employee = { ...data, id: `emp-${crypto.randomUUID().slice(0, 6)}`, status: "Active" };
    setEmployees((p) => [...p, emp]);
    return emp;
  }, []);

  const updateAttendance = useCallback((empId: string, month: string, data: Partial<AttendanceRecord>) => {
    setAttendance((prev) => {
      const idx = prev.findIndex((a) => a.employeeId === empId && a.month === month);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...data };
        return updated;
      }
      const newAtt: AttendanceRecord = {
        id: `att-${crypto.randomUUID().slice(0, 6)}`,
        employeeId: empId, month, totalDays: 26,
        presentDays: 26, absentDays: 0, leaveDays: 0, overtimeHours: 0,
        ...data,
      };
      return [...prev, newAtt];
    });
  }, []);

  const getAttendance = useCallback((empId: string, month: string) =>
    attendance.find((a) => a.employeeId === empId && a.month === month),
  [attendance]);

  const computePayslip = useCallback((emp: Employee, struct: SalaryStructure, att: AttendanceRecord): Omit<Payslip, "id" | "status" | "generatedAt" | "paidAt"> => {
    const daysRatio = att.totalDays > 0 ? (att.presentDays + att.leaveDays) / att.totalDays : 1;
    const basic = Math.round(struct.basic * daysRatio);
    const hra = Math.round((struct.basic * (struct.hra / 100)) * daysRatio);
    const da = Math.round((struct.basic * (struct.da / 100)) * daysRatio);
    const specialAllowance = Math.round(struct.specialAllowance * daysRatio);
    const overtimePay = Math.round((struct.basic / (att.totalDays * 8 || 208)) * (att.overtimeHours || 0) * 1.5);
    const grossEarnings = basic + hra + da + specialAllowance + overtimePay;

    const pf = Math.round(basic * (struct.pf / 100));
    const esi = grossEarnings <= 21000 ? Math.round(grossEarnings * (struct.esi / 100)) : 0;
    const professionalTax = struct.professionalTax || 200;
    const tds = grossEarnings > 50000 ? Math.round(grossEarnings * 0.05) : 0;
    const totalDeductions = pf + esi + professionalTax + tds;
    const netSalary = Math.max(0, grossEarnings - totalDeductions);

    return {
      employeeId: emp.id,
      employeeName: emp.name,
      empCode: emp.empCode,
      month: att.month,
      basic,
      hra,
      da,
      specialAllowance,
      overtimePay,
      grossEarnings,
      pf,
      esi,
      professionalTax,
      tds,
      totalDeductions,
      netSalary,
      daysWorked: att.presentDays,
      totalDays: att.totalDays,
    };
  }, []);

  const generatePayslips = useCallback((month: string): Payslip[] => {
    const generated: Payslip[] = [];
    for (const emp of employees.filter((e) => e.status === "Active")) {
      const existing = payslips.find((p) => p.employeeId === emp.id && p.month === month);
      if (existing) continue;
      const att = attendance.find((a) => a.employeeId === emp.id && a.month === month);
      if (!att) continue;
      const struct = structures.find((s) => s.id === emp.salaryStructureId);
      if (!struct) continue;
      const data = computePayslip(emp, struct, att);
      const ps: Payslip = { ...data, id: `ps-${crypto.randomUUID().slice(0, 8)}`, status: "Generated", generatedAt: new Date().toISOString() };
      generated.push(ps);
    }
    if (generated.length > 0) setPayslips((p) => [...generated, ...p]);
    return generated;
  }, [employees, attendance, structures, payslips]);

  const markPaid = useCallback((id: string) => {
    setPayslips((p) => p.map((ps) => ps.id === id ? { ...ps, status: "Paid", paidAt: new Date().toISOString() } : ps));
  }, []);

  const downloadPayslipPDF = useCallback((ps: Payslip) => {
    const emp = employees.find((e) => e.id === ps.employeeId);
    const doc = new jsPDF();
    const monthLabel = new Date(ps.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });

    // Header
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("AccouPro", 14, 20);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("by hSECURITIES", 14, 26);

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("PAYSLIP", 150, 20);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(monthLabel, 150, 27);

    // Employee details
    doc.setFontSize(10);
    const y = 40;
    doc.text(`Employee: ${ps.employeeName}`, 14, y);
    doc.text(`Code: ${ps.empCode}`, 14, y + 6);
    doc.text(`Designation: ${emp?.designation || "—"}`, 14, y + 12);
    doc.text(`Department: ${emp?.department || "—"}`, 110, y);
    doc.text(`PAN: ${emp?.pan || "—"}`, 110, y + 6);
    doc.text(`Bank: ${emp?.bankName || "—"} — ${emp?.bankAccount || "—"}`, 110, y + 12);
    doc.text(`Days Worked: ${ps.daysWorked} / ${ps.totalDays}`, 14, y + 18);

    // Earnings table
    autoTable(doc, {
      startY: y + 26,
      head: [["Earnings", "Amount (₹)", "Deductions", "Amount (₹)"]],
      body: [
        ["Basic", ps.basic.toLocaleString("en-IN"), "Provident Fund", ps.pf.toLocaleString("en-IN")],
        ["HRA", ps.hra.toLocaleString("en-IN"), "ESI", ps.esi.toLocaleString("en-IN")],
        ["DA", ps.da.toLocaleString("en-IN"), "Professional Tax", ps.professionalTax.toLocaleString("en-IN")],
        ["Special Allowance", ps.specialAllowance.toLocaleString("en-IN"), "TDS", ps.tds.toLocaleString("en-IN")],
        ["Overtime", ps.overtimePay.toLocaleString("en-IN"), "", ""],
        [{ content: "Gross Earnings", styles: { fontStyle: "bold" } }, { content: ps.grossEarnings.toLocaleString("en-IN"), styles: { fontStyle: "bold" } }, { content: "Total Deductions", styles: { fontStyle: "bold" } }, { content: ps.totalDeductions.toLocaleString("en-IN"), styles: { fontStyle: "bold" } }],
      ],
      theme: "grid",
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 41, 69] },
    });

    // Net salary
    const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Net Salary: ₹${ps.netSalary.toLocaleString("en-IN")}`, 14, finalY);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("This is a computer-generated payslip and does not require a signature.", 14, finalY + 12);

    doc.save(`Payslip_${ps.empCode}_${ps.month}.pdf`);
  }, [employees]);

  const exportSalaryReport = useCallback((month: string) => {
    const monthPayslips = payslips.filter((p) => p.month === month);
    if (monthPayslips.length === 0) return;

    const monthLabel = new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    const doc = new jsPDF("landscape");

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Salary Report — ${monthLabel}`, 14, 20);
    doc.setFontSize(8);
    doc.text("AccouPro by hSECURITIES", 14, 26);

    autoTable(doc, {
      startY: 34,
      head: [["Emp Code", "Name", "Basic", "HRA", "DA", "Spl. Allow", "OT", "Gross", "PF", "ESI", "PT", "TDS", "Deductions", "Net Salary", "Status"]],
      body: monthPayslips.map((p) => [
        p.empCode, p.employeeName,
        p.basic.toLocaleString("en-IN"), p.hra.toLocaleString("en-IN"),
        p.da.toLocaleString("en-IN"), p.specialAllowance.toLocaleString("en-IN"),
        p.overtimePay.toLocaleString("en-IN"), p.grossEarnings.toLocaleString("en-IN"),
        p.pf.toLocaleString("en-IN"), p.esi.toLocaleString("en-IN"),
        p.professionalTax.toLocaleString("en-IN"), p.tds.toLocaleString("en-IN"),
        p.totalDeductions.toLocaleString("en-IN"), p.netSalary.toLocaleString("en-IN"),
        p.status,
      ]),
      theme: "grid",
      styles: { fontSize: 7 },
      headStyles: { fillColor: [30, 41, 69] },
    });

    const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    const totalNet = monthPayslips.reduce((s, p) => s + p.netSalary, 0);
    const totalGross = monthPayslips.reduce((s, p) => s + p.grossEarnings, 0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Gross: ₹${totalGross.toLocaleString("en-IN")}    |    Total Net: ₹${totalNet.toLocaleString("en-IN")}    |    Employees: ${monthPayslips.length}`, 14, finalY);

    doc.save(`Salary_Report_${month}.pdf`);
  }, [payslips]);

  return (
    <PayrollContext.Provider value={{
      employees, structures, attendance, payslips,
      addEmployee, updateAttendance, generatePayslips, markPaid,
      downloadPayslipPDF, exportSalaryReport, getAttendance,
    }}>
      {children}
    </PayrollContext.Provider>
  );
}

export function usePayroll() {
  const ctx = useContext(PayrollContext);
  if (!ctx) throw new Error("usePayroll must be used within PayrollProvider");
  return ctx;
}
